import {
  Box,
  FormControlLabel,
  Switch,
  Tab,
  Tabs,
  Typography,
} from "@mui/material";
import { PartDetails } from "../../../../explorer/types/SpareParts/PartDetails";
import {
  BoxIcon,
  BoxTabs,
  BoxText,
  RowGridButton,
  RowTable,
  SectionItems,
  TitleField,
  ValueField,
} from "./PartDetails.style";
import AppCustomTabPanel from "../../AppTabPanel/AppCustomTabPanel";
import Grid from "@mui/material/Grid2";
import { useEffect, useState } from "react";
import useBrands from "../../../../explorer/hooks/useBrands";
import useLanguages from "../../../../explorer/hooks/useLanguages";
import { useTranslation } from "../../../../explorer/hooks/useTranslation";
import { PartApplicability } from "../../../../explorer/types/SpareParts/PartApplicability";
import ServiceProvider from "../../../configuration/ServiceProvider";
import { useAppError } from "../../../hook/useAppError";
import { usePartDetails } from "../../../hook/usePartDetails";
import { AppIconsGears } from "../../AppIcons";
import SkeletonList from "../../../pages/SpareParts/HomePage/SkeletonList/SkeletonList";
import useGoToPage from "../../../hook/useGoToPage";
import { useReduxState } from "../../../../explorer/redux/hooks";
import { Machine, Site } from "../../../../explorer/types/SpareParts";
import { reduxStoreConfig } from "../../../configuration/reduxStoreConfig";
import { SwitchContainer } from "../DrawerPart/DrawerPartDetails.styles";
import useCurrentSite from "../../../hook/useCurrentSite";

type PartDetailsTabsProps = PartDetails & { replacedCode?: string | undefined };

const PartDetailsTabs: React.FC<React.PropsWithChildren<PartDetailsTabsProps>> = (
  props: React.PropsWithChildren<PartDetailsTabsProps>
) => {
  const partDetails = props;
  const [value, setValue] = useState(0);
  const [applicabilities, setApplicabilities] = useState<PartApplicability[]>();
  const brand = useBrands().currentBrandUrl;
  const manufacturer = partDetails.manufacturerCompanyCode;
  const partCode = partDetails.code;
  const { currentLanguage } = useLanguages();
  const [_, setAppError] = useAppError();
  const { closePartDetails } = usePartDetails();
  const [loading, setLoading] = useState(true);
  const { t } = useTranslation();
  const [currentMachine, setCurrentMachine] = useReduxState<Machine>(
    reduxStoreConfig.sparePartsCurrentMachine
  );
  const [currentSite, setCurrentSite] = useCurrentSite();
  const [valueSwitch, setValueSwitch] = useState<boolean>(true);

  const goToPage = useGoToPage();
  const showDetails = partDetails.id;

  const handleChange = (event: React.SyntheticEvent, newValue: number) => {
    setValue(newValue);
  };

  const openReference = (
    event: React.MouseEvent<HTMLButtonElement, MouseEvent>,
    item: PartApplicability
  ) => {
    goToPage.spareParts.goToMachine(
      item.machine.customerSiteCode!,
      item.machine.id!.toString(),
      item.bomItem.naturalKey!,
      item.inBomPartCode
    );

    closePartDetails();
  };

  useEffect(() => {
    if (value === 1 && applicabilities === undefined) {
      loadApplicabilities();
    }
  }, [value, valueSwitch]);

  useEffect(() => {
    if (value === 1) {
      loadApplicabilities();
    }
  }, [valueSwitch]);

  const loadApplicabilities = () => {
    setLoading(true);

    ServiceProvider.SpareParts.getPartApplicabilities(
      brand,
      manufacturer!,
      partCode!,
      currentLanguage!,
      valueSwitch ? currentSite?.customerSiteCode! : undefined
    )
      .then((result) => {
        setApplicabilities(result);
        setLoading(false);
      })
      .catch((error) => setAppError(error));
  };

  const handleToggleSwitch = () => {
    setValueSwitch((prevValue: boolean) => !prevValue);
  };

  return (
    <>
      {showDetails && (
        <SectionItems>
          <BoxTabs sx={{ borderBottom: 1, borderColor: "divider" }}>
            <Tabs
              value={value}
              onChange={handleChange}
              aria-label="basic tabs example"
            >
              <Tab label={t("common.partdetails.itemDescription")} id="1" />
              <Tab label={t("common.partdetails.itemApplicabilities")} id="2" />
            </Tabs>
          </BoxTabs>

          <AppCustomTabPanel value={value} index={0}>
            <Grid sx={{ flexDirection: "column", padding: "10px" }}>
              {partDetails.partDescriptions?.map(
                ({ partCode, description, extDescription }, index) => (
                  <Box key={`row-table_${index}`}>
                    <RowTable>
                      <TitleField size={5}>
                        {t("common.partdetails.item")}
                      </TitleField>
                      <ValueField size={9}>{partCode}</ValueField>
                    </RowTable>
                    <RowTable>
                      <TitleField size={5}>
                        {t("common.partdetails.description")}
                      </TitleField>
                      <ValueField size={9}>{description}</ValueField>
                    </RowTable>
                    <RowTable>
                      <TitleField size={5}>
                        {t("common.partdetails.extDescription")}
                      </TitleField>
                      <ValueField size={9}>{extDescription}</ValueField>
                    </RowTable>
                  </Box>
                )
              )}

              <RowTable key={`row-table_4`}>
                <TitleField size={4}>
                  {t("common.partdetails.weight")}
                </TitleField>
                <ValueField size={8}>
                  {`${partDetails.weight} ${partDetails.weightUnitOfMeasure}`}
                </ValueField>
              </RowTable>
            </Grid>
          </AppCustomTabPanel>

          <AppCustomTabPanel value={value} index={1}>
            <SwitchContainer pl="20px" sx={{ fontSize: 14 }}>
              <FormControlLabel
                control={
                  <Switch value={valueSwitch} onClick={handleToggleSwitch} />
                }
                label={t("spareparts.applicabilities.allSites")}
              />
            </SwitchContainer>
            <Grid>
              {loading ? (
                <SkeletonList mkey={"applicabilities"} />
              ) : !applicabilities || applicabilities.length === 0 ? (
                <Box pl="20px">
                  <Typography sx={{fontSize: 14, textAlign: 'center',}} component="h3">
                    {t("common.spareparts.results.no_results")}
                  </Typography>
                </Box>
              ) : (
                applicabilities?.map((item, idx) => (
                  <RowGridButton
                    key={idx}
                    onClick={(event) => openReference(event, item)}
                  >
                    <BoxText>
                      <Typography component="h3">
                        {`${item.site.customerSiteCode} - ${item.site.customerSiteName}`}
                      </Typography>
                      <Typography>
                        {`${item.machine.serialNumber} - ${item.machine.description}`}
                      </Typography>
                      <Typography>
                        {`${item.bomItem.item} ${item.itemDescription.description}`}
                      </Typography>
                    </BoxText>
                    <BoxIcon>
                      <AppIconsGears />
                    </BoxIcon>
                  </RowGridButton>
                ))
              )}
            </Grid>
          </AppCustomTabPanel>
        </SectionItems>
      )}
    </>
  );
};

export default PartDetailsTabs;
